import { Pressable, StyleSheet, View } from 'react-native';
import type { StyleProp, ViewStyle } from 'react-native';

import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';
import {
  effectiveItemColor,
  panelItemBackgroundColor,
  panelItemForegroundColor,
} from './dataPanelItemBase';
import type { DataPanelItemBaseProps } from './dataPanelItemBase';
import { DataPanelItemAffordances, DataPanelItemValue } from './DataPanelItemParts';

export type DataPanelItemProps = DataPanelItemBaseProps & {
  style?: StyleProp<ViewStyle>;
};

export function DataPanelItem(props: DataPanelItemProps) {
  const theme = useTheme();
  const { title, children, customPanelTemplate, size, clickable, onPanelClick, style, testID } =
    props;

  const itemColor = effectiveItemColor(props);
  const containerStyle: ViewStyle = {
    backgroundColor: panelItemBackgroundColor(theme, itemColor) ?? theme.colors.surface,
    paddingVertical: theme.spacing(2),
    paddingHorizontal: theme.spacing(3),
    ...((size ?? 'stretch') === 'stretch' ? { flex: 1 } : {}),
  };

  if (customPanelTemplate != null) {
    return (
      <View testID={testID} style={[containerStyle, style]}>
        {customPanelTemplate}
      </View>
    );
  }

  const foreground = panelItemForegroundColor(theme, itemColor);

  return (
    <Pressable
      accessibilityRole={clickable === true ? 'button' : undefined}
      disabled={clickable !== true || onPanelClick == null}
      onPress={() => void onPanelClick?.()}
      testID={testID}
      style={({ pressed }) => [
        styles.item,
        containerStyle,
        pressed && clickable === true && styles.pressed,
        style,
      ]}
    >
      <View style={styles.body}>
        {title != null ? (
          <Text variant="caption" color={foreground ?? theme.colors.textMuted}>
            {title}
          </Text>
        ) : null}

        <DataPanelItemValue props={props} color={foreground} />

        {children}
      </View>

      <DataPanelItemAffordances props={props} />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  item: { flexDirection: 'row', alignItems: 'center' },
  body: { flex: 1, gap: 1 },
  pressed: { opacity: 0.7 },
});
